import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { StatCard } from "../components/common/StatCard";
import { DataTable } from "../components/common/DataTable";
import { FilterBar } from "../components/common/FilterBar";
import { PageHeader } from "../components/common/PageHeader";
import { useDataFetching } from "../hooks/useDataFetching";
import { useFiltering } from "../hooks/useFiltering";
import { eventsAPI, schoolsAPI } from "../api";
import { QUERY_KEYS } from "../utils/constants";
import { formatDateTime } from "../utils/formatters";
import { getSchoolName, handleSchoolId } from "../utils/helpers";
import { SchoolFilter } from "../components/filters/SchoolFilter";
import { Switch } from "../components/ui/switch";
import { Calendar, MapPin, Home, Eye, EyeOff, Pin } from "lucide-react";
import { Event, ApiDataResponse, FilterFunction } from "../types";

export function EventsPage() {
  const {
    data: eventsData,
    isLoading,
    error,
  } = useDataFetching(QUERY_KEYS.EVENTS, {
    queryFn: eventsAPI.getAllEvents,
  });

  const { data: schoolsData } = useDataFetching(QUERY_KEYS.SCHOOLS, {
    queryFn: schoolsAPI.getAllSchools,
  });

  const events = Array.isArray(eventsData) ? eventsData : ((eventsData as ApiDataResponse<Event[]>)?.data || []);
  const schools = Array.isArray(schoolsData) ? schoolsData : [];

  const {
    searchTerm,
    setSearchTerm,
    filters,
    updateFilter,
    filteredData: filteredEvents,
  } = useFiltering({
    data: events,
    searchFields: ['name', 'description'],
    filterFn: ((event: Event, filters) => {
      const schoolMatch = filters.schoolId
        ? handleSchoolId(event.school_id) === filters.schoolId
        : true;
      return schoolMatch;
    }) as FilterFunction<Event>,
  });

  const outdoorEvents = filteredEvents.filter(
    (event: Event) => event.event_type === "outdoor"
  ).length;
  const indoorEvents = filteredEvents.filter(
    (event: Event) => event.event_type === "indoor"
  ).length;
  const publicEvents = filteredEvents.filter(
    (event: Event) => event.visibility === "public"
  ).length;
  const privateEvents = filteredEvents.filter(
    (event: Event) => event.visibility === "private"
  ).length;

  const pinnedEvents = filteredEvents.filter((event: Event) => event.pinned).length;

  const queryClient = useQueryClient();
  const pinMutation = useMutation({
    mutationFn: ({ eventId, pinned }: { eventId: string; pinned: boolean }) =>
      eventsAPI.setEventPinned(eventId, pinned),
    // A recurring event pins its whole series, so refetch rather than patch one row.
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENTS }),
  });

  const handleSchoolChange = useCallback((schoolId: string) => {
    updateFilter('schoolId', schoolId);
  }, [updateFilter]);

  const getTypeBadge = (eventType: string) => {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
          eventType === "outdoor"
            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
            : "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200"
        }`}
      >
        {eventType === "outdoor" ? <MapPin className="h-3 w-3" /> : <Home className="h-3 w-3" />}
        {eventType}
      </span>
    );
  };

  const getVisibilityBadge = (visibility: string) => {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
          visibility === "public"
            ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
            : "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200"
        }`}
      >
        {visibility === "public" ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
        {visibility}
      </span>
    );
  };

  const columns = [
    {
      key: 'image_url',
      header: 'Image',
      render: (event: Event) => (
        <div className="flex items-center">
          {event.image_url ? (
            <img
              src={event.image_url}
              alt={event.name}
              className="h-12 w-12 rounded-lg object-cover border border-border"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                e.currentTarget.src = 'https://via.placeholder.com/48?text=Event';
              }}
            />
          ) : (
            <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center border border-border">
              <Calendar className="h-6 w-6 text-muted-foreground" />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'pinned',
      header: 'Pinned',
      render: (event: Event) => (
        <Switch
          checked={Boolean(event.pinned)}
          disabled={pinMutation.isPending && pinMutation.variables?.eventId === event._id}
          onCheckedChange={(pinned) => pinMutation.mutate({ eventId: event._id, pinned })}
          aria-label={event.pinned ? `Unpin ${event.name}` : `Pin ${event.name} to the top`}
        />
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (event: Event) => (
        <span className="font-medium">{event.name}</span>
      ),
    },
    {
      key: 'event_type',
      header: 'Type',
      render: (event: Event) => getTypeBadge(event.event_type),
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (event: Event) => getVisibilityBadge(event.visibility),
    },
    {
      key: 'school_id',
      header: 'School',
      render: (event: Event) => getSchoolName(handleSchoolId(event.school_id), schools),
    },
    {
      key: 'date_and_time',
      header: 'Date',
      render: (event: Event) => (
        <span className="text-muted-foreground">
          {formatDateTime(event.date_and_time)}
        </span>
      ),
    },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState type="table" />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <ErrorState
          title="Error loading events data"
          message="Please try refreshing the page or log in again"
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="responsive-container">
        <div className="space-y-6">
          <PageHeader
            title="Events"
            description="Manage and view all events on the Walky platform"
          />

          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-6">
            <StatCard
              title="Total Events"
              value={filteredEvents.length}
              description="All events"
              icon={<Calendar className="h-4 w-4" />}
              variant="blue"
              isLoading={isLoading}
            />

            <StatCard
              title="Pinned"
              value={pinnedEvents}
              description="Shown first in the app"
              icon={<Pin className="h-4 w-4" />}
              variant="orange"
              isLoading={isLoading}
            />

            <StatCard
              title="Outdoor Events"
              value={outdoorEvents}
              description="Outdoor activities"
              icon={<MapPin className="h-4 w-4" />}
              variant="green"
              isLoading={isLoading}
            />

            <StatCard
              title="Indoor Events"
              value={indoorEvents}
              description="Indoor activities"
              icon={<Home className="h-4 w-4" />}
              variant="cyan"
              isLoading={isLoading}
            />

            <StatCard
              title="Public Events"
              value={publicEvents}
              description="Public access"
              icon={<Eye className="h-4 w-4" />}
              variant="purple"
              isLoading={isLoading}
            />

            <StatCard
              title="Private Events"
              value={privateEvents}
              description="Private access"
              icon={<EyeOff className="h-4 w-4" />}
              variant="amber"
              isLoading={isLoading}
            />
          </div>

          <DataTable
            data={filteredEvents}
            columns={columns}
            title="All Events"
            headerActions={
              <FilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                searchPlaceholder="Search events by name or description..."
                resultCount={{
                  filtered: filteredEvents.length,
                  total: events.length,
                }}
              >
                <SchoolFilter
                  selectedSchoolId={filters.schoolId ? String(filters.schoolId) : ''}
                  onSchoolChange={handleSchoolChange}
                />
              </FilterBar>
            }
            emptyMessage="No events found"
            emptyDescription={
              searchTerm || filters.schoolId
                ? "Try adjusting your search filters"
                : "No events have been created yet"
            }
            isLoading={isLoading}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}                   